import { Button, ButtonMode, ButtonSize, Loader } from '@creditsights/styleguide-web';
import { decode } from 'he';
import { ReactElement, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import useFetchAnalystArticles from '../../hooks/articles/useFetchAnalystArticles';
import { formatDate } from '../../utils/date.utils';
import { hasMoreData } from '../../utils/page.utils';
import { filterCompanyTags } from '../../utils/tag.utils';
import { AnalystBioEvents } from '../../utils/trackUsage.utils';
import './AnalystArticles.scss';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function AnalystArticles({ analystId, noPublications, analystBioDataTracking }): ReactElement {
  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useFetchAnalystArticles(analystId);

  useEffect(() => {
    if (!isLoading && data?.pages[0]?.length === 0) {
      noPublications();
    }
  }, [data, isLoading]);

  const loadMoreArticles = () => {
    analystBioDataTracking({ event: AnalystBioEvents.ANALYST_BIO_LOAD_MORE_ARTICLES });
    fetchNextPage();
  };

  const renderTags = (tags) => {
    const companyTags = filterCompanyTags(tags)
      .slice(0, 5)
      .map((tag) => (
        <NavLink key={tag.tagId} className="ticker" to={`/companies/${tag.tagId.replace('company/', '')}`}>
          {tag.ticker}
        </NavLink>
      ));

    if (companyTags.length > 0) {
      return (
        <>
          <div className="separator" />
          <div className="tags">{companyTags}</div>
        </>
      );
    }
    return <div />;
  };

  const articlesClickHandler = (articleId: string) => {
    analystBioDataTracking({ event: AnalystBioEvents.ANALYST_BIO_ARTICLE_NAVIGATION, articleId });
  };

  if (isLoading) {
    return <Loader className="cs-loader" position="center" />;
  }
  return (
    <div className="cs-analyst-articles">
      <div className="section-title">
        <div className="text">Recently Published Articles</div>
      </div>
      {!isLoading &&
        data &&
        data.pages.map((articles) => (
          <div className="page">
            {articles &&
              articles.map((article) => {
                return (
                  <div key={`article-${article.id}`} className="article">
                    <NavLink to={`/articles/${article.id}`} className="title" onClick={() => articlesClickHandler(article.id)}>
                      {article.content.title}
                    </NavLink>
                    <div className="info">
                      <div className="date">{formatDate(article.publishDate)}</div>
                      {renderTags(article.tags)}
                    </div>
                    <div className="summary">{article.content.summary ? decode(article.content.summary) : ''}</div>
                  </div>
                );
              })}
          </div>
        ))}
      {hasMoreData(data, 25) && (
        <div className="next-page">
          <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isFetchingNextPage} disabled={isFetchingNextPage} onClick={loadMoreArticles}>
            Load 25 more
          </Button>
        </div>
      )}
    </div>
  );
}

export default AnalystArticles;
